function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeProperty(item) {
  return {
    ...item,
    Latitud: toNumber(item.Latitud),
    Longitud: toNumber(item.Longitud),
    Precio: toNumber(item.Precio) ?? 0,
    SuperficieM2: toNumber(item.SuperficieM2),
    SuperficieCubiertaM2: toNumber(item.SuperficieCubiertaM2),
    Ambientes: toNumber(item.Ambientes),
    Dormitorios: toNumber(item.Dormitorios),
    Banios: toNumber(item.Banios),
    Antiguedad: toNumber(item.Antiguedad),
    Expensas: toNumber(item.Expensas),
    Frente: toNumber(item.Frente),
    Fondo: toNumber(item.Fondo),
  };
}

export const SERVICE_KEYS = ['agua', 'luz', 'gas', 'cloacas', 'asfalto'];
export const DEED_KEYS = ['escritura', 'boleto', 'posesion', 'tramite'];
export const LAND_TYPE = 'Terreno';

export function isLand(property) {
  return property?.TipoPropiedad === LAND_TYPE;
}

export function getServices(property) {
  const raw = String(property?.Servicios || '').split(',').map((item) => item.trim());
  return SERVICE_KEYS.filter((key) => raw.includes(key));
}

export function getPricePerM2(property) {
  if (property?.TipoOperacion !== 'VENTA' || !(property.SuperficieM2 > 0) || !(property.Precio > 0)) {
    return null;
  }
  return property.Precio / property.SuperficieM2;
}

export function formatDimensions(property, locale) {
  if (!(property?.Frente > 0) || !(property?.Fondo > 0)) {
    return '';
  }
  return `${formatNumber(property.Frente, locale, 2)} × ${formatNumber(property.Fondo, locale, 2)} m`;
}

// Must match slugify() in backend/src/controllers/metaController.js (sitemap URLs).
export function slugify(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getDirectionsUrl(property) {
  if (!hasCoords(property)) {
    return '';
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${property.Latitud},${property.Longitud}`;
}

export function hasGarage(property) {
  return property?.Cochera === 1 || property?.Cochera === '1' || property?.Cochera === true;
}

export function hasCoords(property) {
  return Number.isFinite(property?.Latitud) && Number.isFinite(property?.Longitud);
}

export function formatPrice(amount, currency, locale) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency || 'USD',
    currencyDisplay: 'code',
    maximumFractionDigits: 0,
  }).format(Number(amount || 0)).replace(/\u00a0/g, ' ');
}

export function formatNumber(value, locale, maxDigits = 0) {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: maxDigits }).format(Number(value || 0));
}

export function formatArea(value, locale) {
  if (!Number(value)) {
    return '';
  }
  return `${formatNumber(value, locale, 1)} m²`;
}

export function getOperationLabel(operation, t) {
  if (operation === 'VENTA') {
    return t.onSale;
  }
  if (operation === 'ALQUILER') {
    return t.forRent;
  }
  return operation || '';
}

export function getPropertyImages(property) {
  if (!property) {
    return [];
  }
  const list = property.Imagenes?.length
    ? property.Imagenes.map((img) => img?.UrlImagen)
    : [property.ImagenPrincipal];
  return [...new Set(list.filter(Boolean))];
}

export function getLocationLine(property) {
  return [property?.Ciudad, property?.Provincia].filter(Boolean).join(', ');
}

export function getKeyFeatures(property, t, locale) {
  if (!property) {
    return [];
  }
  if (isLand(property)) {
    const perM2 = getPricePerM2(property);
    return [
      property.SuperficieM2 ? { key: 'area', value: formatArea(property.SuperficieM2, locale) } : null,
      formatDimensions(property, locale) ? { key: 'dimensions', value: formatDimensions(property, locale) } : null,
      perM2 ? { key: 'perM2', value: `${formatPrice(perM2, property.Moneda, locale)}${t.perM2}` } : null,
    ].filter(Boolean);
  }
  return [
    property.SuperficieM2 ? { key: 'area', value: formatArea(property.SuperficieM2, locale) } : null,
    property.Dormitorios ? { key: 'bedrooms', value: `${property.Dormitorios} ${t.bedroomsShort}` } : null,
    property.Banios ? { key: 'bathrooms', value: `${property.Banios} ${t.bathroomsShort}` } : null,
    !property.Dormitorios && property.Ambientes ? { key: 'rooms', value: `${property.Ambientes} ${t.roomsShort}` } : null,
  ].filter(Boolean);
}

export function getPropertyUrl(id) {
  return `/propiedad/${id}`;
}

export function getAbsoluteUrl(path) {
  if (typeof window === 'undefined') {
    return path;
  }
  return new URL(path, window.location.origin).toString();
}

export function buildWhatsappHref(number, message) {
  if (!number) {
    return '';
  }
  const clean = String(number).replace(/[^\d]/g, '');
  return `https://wa.me/${clean}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
}

export function propertyWhatsappMessage(property, t, intent = 'inquiry') {
  const template = intent === 'visit' ? t.waVisitMessage : t.waPropertyMessage;
  return template
    .replace('{title}', property?.Titulo || '')
    .replace('{city}', property?.Ciudad || '')
    .replace('{url}', getAbsoluteUrl(getPropertyUrl(property?.IdPropiedad)));
}

export function isFeatured(property) {
  return Number(property?.Destacada) === 1;
}

// Most recently featured first; the API returns FechaDestacada as an ISO string.
export function getFeatured(properties) {
  return properties
    .filter(isFeatured)
    .sort((a, b) => String(b.FechaDestacada || '').localeCompare(String(a.FechaDestacada || '')));
}

// The newest pick from /admin wins; otherwise the highest priced USD sale with a photo is highlighted.
export function pickFeatured(properties) {
  const [chosen] = getFeatured(properties);
  if (chosen) {
    return chosen;
  }
  const withImage = properties.filter((item) => item.ImagenPrincipal);
  const sales = withImage.filter((item) => item.TipoOperacion === 'VENTA' && item.Moneda === 'USD');
  const pool = sales.length ? sales : withImage;
  return pool.reduce((best, item) => (!best || item.Precio > best.Precio ? item : best), null);
}

export function sortProperties(list, order) {
  const sorted = [...list];
  if (order === 'price-asc') {
    sorted.sort((a, b) => a.Precio - b.Precio);
  } else if (order === 'price-desc') {
    sorted.sort((a, b) => b.Precio - a.Precio);
  } else if (order === 'area-desc') {
    sorted.sort((a, b) => (b.SuperficieM2 || 0) - (a.SuperficieM2 || 0));
  }
  return sorted;
}
